import moment from "moment";
import { call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import * as globalSelectors from "Store/global/selectors";
// Students
import * as classActions from "Store/classrooms/actions";
import * as classApi from "Store/classrooms/api";
import {
  addInstructorToClassroomApi,
  assignClassroomAssignmentToStudent,
  assignLessonToClassroomApi,
  deleteClassroomApi,
  deleteClassroomSessionApi,
  fetchClassroomApi,
  fetchClassroomCalendarLessons,
  fetchClassroomCalendarTests,
  fetchClassroomCalendarWorksheets,
  fetchClassroomLessonApi,
  fetchClassroomOverdueStats,
  fetchClassroomProblemsStats,
  fetchClassroomSummaryApi,
  fetchClassroomTestApi,
  fetchClassroomTestSectionApi,
  fetchClassroomWorksheetApi,
  fetchClassroomWorksheetByCategoryApi,
  fetchClassroomWorksheetListApi,
  removeInstructorFromClassroomApi,
  unassignLessonFromClassroomApi,
  unassignTestFromClassroomApi,
  unassignWorksheetFromClassApi, updateClassroomTestNotifyApi,
  updateClassroomTestSectionsApi
} from "Store/classrooms/api";
import * as classTypes from "Store/classrooms/constants";
import * as globalActions from "Store/global/actions";
import {
  setActionSpinner,
  setFetchDetailsSpinner,
  setFetchSpinner,
  setGlobalTemplateLoader,
  setReportSpinner
} from "Store/global/actions";
import {
  fetchLessonTemplateApi,
  fetchTemplateTestApi,
  fetchWorksheetTemplateApi
} from "Store/global/api";
import {
  handleFetchClassrooms,
  setClassroomActiveLesson,
  setClassroomCalendarLoading,
  setClassroomLessons,
  setClassroomTests,
  setClassroomWorksheets
} from "./actions";
// Selectors
import ERRORS from "constants/localization/errors";
import INFOS from "constants/localization/infos";
import SUCCESSES from "constants/localization/successes";
import Router from "next/router";
import { getEstimateTime } from "Store/courseTemplates/saga";
import { convertMetaToPagination, downloadFile, renderApiParameters } from "Store/global/saga";
import {
  handleAssignStudentToClassroomSaga,
  handleRemoveStudentFromClassroomSaga
} from "Store/students/saga";
import { removeDuplicateFromArrayObjects } from "utils/objectsHelper";
import { backendDateFormat } from "utils/timeHelpers";
import { Toast } from "utils/toast";
import { makeSelectActiveClassroom, makeSelectClassroomWorksheetsList } from "./selectors";
import _ from "lodash";
import {updateStudentTestNotifyApi} from "../students/studentsTests/api";
function* handleAssignClassroomTestSaga(action) {
  const { payload, source } = action;
  yield put(globalActions.setActionSpinner(true));
  try {
    const { error } = yield classApi.assignTestToClassroomApi(payload);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleAssignClassroomTest, error);
    } else {
      if (source === "list") {
        yield call(handleFetchClassroomTestsSaga);
      } else {
        yield put(setClassroomCalendarLoading(true));
        yield call(handleFetchClassroomCalendarTestsSaga);
        yield put(setClassroomCalendarLoading(false));
      }
      Toast.success(SUCCESSES.handleAssignClassroomTest);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleAssignClassroomTest, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleAssignSessionToClassroomSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  const { payload } = action;
  try {
    const { error } = yield classApi.assignSessionToClassroomApi(payload);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      Toast.error(ERRORS.handleAssignSessionToClassroomSaga, error);
    } else {
      yield call(handleFetchClassroomSaga, { payload: payload.classroom_id });
      Toast.success(SUCCESSES.handleAssignSessionToClassroomSaga);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleAssignSessionToClassroomSaga, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleUnassignLessonFromClassroomSaga(action: classTypes.IUnassignClassroomLesson) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const activeClassroom: Classroom = yield select(makeSelectActiveClassroom);
    const { classroom_id, classroom_lessons } = action.payload;
    const lessonProblemsCount = classroom_lessons
      .map((lesson) => lesson.lesson.problem_count)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
    const body: UnassignLessonsFromClassRequest = {
      classroom_id,
      classroom_lessons: classroom_lessons.map((lesson) => lesson.id)
    };
    const estimateTimeAssigningLessons = getEstimateTime(
      lessonProblemsCount,
      activeClassroom.active_students
    );
    const { error } = yield classApi.unassignLessonFromClassroomApi(body);
    if (error) {
      Toast.error(ERRORS.handleUnAssignLessonFromClass, error);
    } else {
      yield put(globalActions.setActionSpinner(false));
      Toast.info(INFOS.handleUnassignLessonsToClass, {
        autoClose: estimateTimeAssigningLessons,
        position: "bottom-left"
      });
      yield delay(estimateTimeAssigningLessons);
      yield put(classActions.handleFetchClassroomLessons());
      Toast.success(SUCCESSES.handleUnAssignLessonFromClass);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUnAssignLessonFromClass, error);
  }
}

function* handleUnassignTestFromClassroomSaga(action) {
  try {
    yield put(globalActions.setActionSpinner(true));
    const { error } = yield classApi.unassignTestFromClassroomApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUnAssignTestFromClass, error);
    }
    Toast.success(SUCCESSES.handleUnAssignTestFromClass);
    yield call(handleFetchClassroomTestsSaga);
  } catch (error) {
    Toast.error(ERRORS.handleUnAssignTestFromClass, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleRescheduleClassroomLessonsSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  yield put(setClassroomCalendarLoading(true));
  try {
    const { payload, source } = action;

    const lessons = payload.lessons.map((lesson) => ({
      ...lesson,
      assignment_date: moment(lesson.assignment_date).format(backendDateFormat),
      due_date: !!lesson.due_date ? moment(lesson.due_date).format(backendDateFormat) : null
    }));

    const data = {
      ...payload,
      lessons
    };

    const { error } = yield classApi.rescheduleClassroomLessonsApi(data);
    if (error) {
      Toast.error(ERRORS.handleRescheduleClassLessons, error);
    } else {
      Toast.success(SUCCESSES.handleRescheduleClassLessons);
      if (source === "list") {
        yield put(classActions.handleFetchClassroomLessons());
      } else {
        yield call(handleFetchClassroomCalendarLessonsSaga);
      }
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleClassLessons, error);
  } finally {
    yield put(setClassroomCalendarLoading(false));
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleRescheduleClassroomTests(action) {
  yield put(globalActions.setActionSpinner(true));
  yield put(setClassroomCalendarLoading(true));
  try {
    const { payload, source } = action;
    const { error } = yield classApi.rescheduleClassroomTestsApi(payload);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleRescheduleClassTests, error);
    }
    if (source === "list") {
      yield call(handleFetchClassroomTestsSaga);
    } else {
      yield call(handleFetchClassroomCalendarTestsSaga);
    }
    Toast.success(SUCCESSES.handleRescheduleClassroomTests);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleClassTests, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
    yield put(setClassroomCalendarLoading(false));
  }
}

function* handleUpdateClassroomSessionSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  yield put(setClassroomCalendarLoading(true));
  try {
    const { error } = yield classApi.updateClassroomSessionApi(action.payload);

    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleUpdateClassroomSessionSaga, error);
    }
    // Select active class
    const activeClass = yield select(makeSelectActiveClassroom);
    if (!activeClass) {
      yield put(globalActions.setErrorSignal(true));
      return;
    }
    yield delay(500);

    yield put(classActions.handleFetchClassroom(activeClass.id));
    Toast.success(SUCCESSES.handleUpdateClassroomSessionSaga);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleUpdateClassroomSessionSaga, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
    yield put(setClassroomCalendarLoading(false));
  }
}

function* handleAssignWorksheetsToClassroomSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { classroom_id, worksheets, assignment_date, due_date, problemsCount } = action.payload;
    const classroom: Classroom = yield select(makeSelectActiveClassroom);
    const assignedWorksheets = yield select(makeSelectClassroomWorksheetsList);
    const estimateTimeAssigning =
      ((problemsCount * Number(classroom.students.length)) / 130) * 1000;

    Toast.info(INFOS.handleAssignWorksheetToClass, {
      autoClose: estimateTimeAssigning,
      position: "bottom-left"
    });

    const payload = {
      classroom_id,
      assignment_date,
      due_date,
      worksheets: worksheets
    };
    yield classApi.assignWorksheetToClassroomApi(payload);

    yield put(globalActions.setActionSpinner(false));
    const { data, error } = yield delay(
      estimateTimeAssigning,
      fetchClassroomWorksheetListApi(classroom.id, "")
    );

    if (error) {
      yield put(globalActions.setErrorSignal(true));
      Toast.error(ERRORS.handleAssignWorksheetToClass, error);
    } else {
      yield call(handleFetchClassroomWorksheetsSaga);
      if (action.source === "calendar") {
        yield put(setClassroomCalendarLoading(true));
        yield call(handleFetchClassroomCalendarWorksheetsSaga);

        yield put(setClassroomCalendarLoading(false));
      } else if (action.source === "list") {
        yield call(handleFetchClassroomWorksheetsSaga);
      }
      Toast.success(SUCCESSES.handleAssignWorksheetToClass);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleAssignWorksheetToClass, error);
  } finally {
  }
}

function* handleFetchClassReportPDFSaga(action) {
  Toast.info(INFOS.generatingReportInfo);
  const { classroom_id, printing, classroom_name } = action.payload;
  yield put(setActionSpinner(true));
  yield put(setReportSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "classroomTests");
    const { error: testsListError, data } = yield classApi.fetchClassroomTestListApi(
      classroom_id,
      parameters
    );

    if (testsListError) {
      Toast.error(testsListError);
    }
    if (data.length !== 0) {
      const testIds = data.map((test) => test.id);
      const body = {
        test_ids: testIds,
        printing: printing
      };
      const { data: file, error } = yield classApi.fetchClassroomReportPDFApi(classroom_id, body);
      if (error) {
        Toast.error(ERRORS.handleFetchClassReportPDF, error);
      } else {
        yield call(downloadFile, file, `${classroom_name}-report.pdf`);
      }
    } else {
      Toast.warning(ERRORS.handleFetchClassReportPDFNoClassroomTests);
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassReportPDF, error);
  } finally {
    yield put(setActionSpinner(false));
    yield put(setReportSpinner(false));
  }
}

function* handleAssignLessonsToClassroomSaga(action: classTypes.IHandleAssignLessonsToClass) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { payload, source, problemsCount } = action;
    const classroom: Classroom = yield select(makeSelectActiveClassroom);
    const estimateTimeAssigning =
      ((problemsCount * Number(classroom.students.length)) / 130) * 1000;
    Toast.info(INFOS.handleAssignLessonToClass, {
      autoClose: estimateTimeAssigning,
      position: "bottom-left"
    });
    const { error } = yield assignLessonToClassroomApi(payload);
    if (error) {
      Toast.error(ERRORS.handleAssignLessonsToClassSaga, error.message);
    } else {
      yield put(globalActions.setActionSpinner(false));
      yield delay(estimateTimeAssigning, classApi.fetchClassroomLessonListApi(classroom.id, ""));
      if (source === "list") {
        yield put(classActions.handleFetchClassroomLessons());
      } else {
        yield put(setClassroomCalendarLoading(true));
        yield call(handleFetchClassroomCalendarLessonsSaga);
        yield put(setClassroomCalendarLoading(false));
      }
      Toast.success(SUCCESSES.handleAssignLessonsToClassSaga);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleAssignLessonsToClassSaga, error.message);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleDeleteMultipleItemsFromClassSaga(action) {
  yield put(setActionSpinner(true));
  const classroom_id = action.classId;
  try {
    let errors = [];
    for (const [section] of Object.entries(action.payload)) {
      const requestData = {
        [`classroom_${section}`]: action.payload[section],
        classroom_id
      };
      switch (section) {
        case "sessions":
          for (const id of requestData.classroom_sessions) {
            const { error: sessionsError } = yield deleteClassroomSessionApi({
              classroom_session_id: id
            });
            if (sessionsError) errors.push(sessionsError);
          }

          break;
        case "lessons":
          const { error: LessonsError } = yield call(unassignLessonFromClassroomApi, requestData);
          if (LessonsError) errors.push(LessonsError);
          break;
        case "worksheets":
          const { error: WorksheetsError } = yield call(unassignWorksheetFromClassApi, requestData);
          if (WorksheetsError) errors.push(WorksheetsError);
          break;
        case "tests":
          const { error: TestsError } = yield call(unassignTestFromClassroomApi, requestData);
          if (TestsError) errors.push(TestsError);
          break;
      }
    }

    yield delay(1000);
    /* Fetching only changed data */
    for (const [section] of Object.entries(action.payload)) {
      switch (section) {
        case "lessons":
          yield call(handleFetchClassroomLessonsSaga);
          break;
        case "worksheets":
          yield call(handleFetchClassroomWorksheetsSaga);
          break;
        case "tests":
          yield call(handleFetchClassroomTestsSaga);
          break;
      }
    }
    yield call(handleFetchClassroomSaga, { payload: classroom_id });
    if (errors.length > 0) {
      Toast.error(errors.join(", "));
    } else {
      Toast.success(SUCCESSES.handleDeleteMultipleItemsFromClassSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleDeleteMultipleItemsFromClassSaga, error.message);
  } finally {
    yield put(setActionSpinner(false));
  }
}

/* CALENDAR SAGAS */
export function* handleFetchClassroomCalendarLessonsSaga() {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    const { data, error } = yield fetchClassroomCalendarLessons(classroom.id);
    if (error) {
      Toast.error(ERRORS.handleFetchClassroomCalendarLessonsSaga);
    } else {
      yield put(setClassroomLessons(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomCalendarLessonsSaga);
  }
}

export function* handleFetchClassroomCalendarWorksheetsSaga() {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    const { data, error } = yield fetchClassroomCalendarWorksheets(classroom.id);
    if (error) {
      Toast.error(ERRORS.handleFetchClassroomCalendarWorksheetsSaga);
    } else {
      yield put(setClassroomWorksheets(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomCalendarLessonsSaga);
  }
}

export function* handleFetchClassroomCalendarTestsSaga() {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    const { data, error } = yield fetchClassroomCalendarTests(classroom.id);
    if (error) {
      throw new Error(error);
    } else {
      yield put(setClassroomTests(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomCalendarLessonsSaga, error);
  }
}

export function* handleFetchClassCalendarDataSaga(action) {
  yield put(globalActions.setFetchCalendarSpinner(true));
  try {
    yield call(handleFetchClassroomCalendarLessonsSaga);
    yield call(handleFetchClassroomCalendarWorksheetsSaga);
    yield call(handleFetchClassroomCalendarTestsSaga);
    yield call(handleFetchClassroomSaga, { payload: action.payload });
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassCalendarDataSaga, error.message);
  } finally {
    yield put(globalActions.setFetchCalendarSpinner(false));
  }
}

/* */

function* handleOpenLessonDetailsSaga(item: Lesson) {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    const { data: classroomLesson } = yield fetchClassroomLessonApi(classroom.id, item.id);
    const { data: template, error: templateError } = yield fetchLessonTemplateApi(
      classroomLesson.lesson_id
    );

    if (templateError) {
      Toast.error(ERRORS.handleOpenLessonDetailsSaga, templateError);
      return yield put(setClassroomActiveLesson(null));
    }
    let sortedStudentProblems = [];
    let problems = {};
    switch (template?.type.label) {
      case "Drill":
        problems = {
          drill: template.problems.map((problem) => ({
            ...problem,
            ...classroomLesson.problems.find((lessonProblem) => lessonProblem.id === problem.id)
          }))
        };
        break;
      case "Workbook Lesson":
      case "Module":
        template.sections.forEach((section) => {
          problems = {
            ...problems,
            [section.name]: section.lesson_problems.map((problem) => ({
              ...problem,
              ...classroomLesson.problems.find((lessonProblem) => lessonProblem.id === problem.id)
            }))
          };
        });
        sortedStudentProblems = classroomLesson.students.map((student) => ({
          ...student,
          sortedProblems: _.groupBy(student.problems, "lesson_section_name")
        }));
        break;
      default:
        break;
    }
    const lesson = {
      ...classroomLesson,
      problems,
      sections: template.sections,
      students: sortedStudentProblems.length ? sortedStudentProblems : classroomLesson.students
    };
    yield put(setClassroomActiveLesson(lesson));
    return lesson;
  } catch (error) {
    Toast.error(ERRORS.handleOpenLessonDetailsSaga, error);
    put(setClassroomActiveLesson(null));
    yield put(setFetchDetailsSpinner(false));
  }
}

function* handleOpenSelectedItemDetailsClassroomSaga(action) {
  yield put(setFetchDetailsSpinner(true));
  const { type, item } = action.payload;
  try {
    switch (type) {
      case "lessons":
        yield call(handleOpenLessonDetailsSaga, item);
        break;
      case "worksheets":
        yield call(handleOpenWorksheetDetailsSaga, item);
        break;
      case "tests":
        yield call(handleOpenTestDetailsSaga, item);
        break;
      default:
        break;
    }
  } catch (error) {
    Toast.error(ERRORS.handleOpenSelectedItemDetailsClassroomSaga, error.message);
  } finally {
    yield put(setFetchDetailsSpinner(false));
  }
}

function* handleUpdateClassroomTestSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const activeClass = yield select(makeSelectActiveClassroom);
    const {
      payload: { classroom_test_id, assignment_date, due_date, test_section_ids, notify, notify_emails },
      testChanges
    } = action;

    const payload = {
      classroom_id: activeClass?.id,
      tests: [
        {
          classroom_test_id,
          due_date,
          assignment_date
        }
      ]
    };
    yield call(handleRescheduleClassroomTests, { payload, source: action.source });

    if (testChanges && testChanges?.sections) {
      const payload = {
        classroom_test_id,
        test_section_ids: test_section_ids
      };
      const { error } = yield updateClassroomTestSectionsApi(payload);
      if (error) {
        return Toast.error(ERRORS.handleUpdateClassroomTestSections, error);
      }
    }

    if (testChanges?.notify) {
      const payload = {
        classroom_test_id,
        notify,
        notify_emails
      };
      yield call(handleUpdateTestNotify, { payload });
    }

    if (assignment_date || due_date || !!test_section_ids.length) {
      yield call(handleFetchClassroomTestsSaga);
    }
    Toast.success(SUCCESSES.handleUpdateClassroomTest);
  } catch (error) {
    Toast.error(ERRORS.handleUpdateClassroomTest, error);
    yield put(globalActions.setErrorSignal(true));
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleUpdateTestNotify(action) {
  try {
    const { error } = yield updateClassroomTestNotifyApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateTestNotify, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestNotify, error);
  }
}

function* handleCreateClassroomSaga(action: classTypes.IHandleCreateClassroom) {
  yield put(setActionSpinner(true));

  try {
    const { locations, students, instructors } = action.payload;

    const classroomBody = {
      ...action.payload,
      end_date: action.payload.end_date ? moment(action.payload.end_date).format("YYYY-MM-DD") : null,
      start_date: action.payload.start_date ? moment(action.payload.start_date).format("YYYY-MM-DD") : null,
      location: locations[0].id,
      instructors: instructors.map((instructor) => instructor.id),
      students: students.map((student) => student.id)
    };
    const { error } = yield classApi.createClassroomApi(classroomBody);
    if (error) {
      Toast.error(ERRORS.createClassroom, error);
    } else {
      Toast.success(SUCCESSES.handleCreateNewClassroomSaga);
      yield put(handleFetchClassrooms(true));
    }
  } catch (error) {
    return Toast.error(ERRORS.createClassroom, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

// =============== REFACTORED =================
function* handleDeleteClassroomSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield deleteClassroomApi({ class_id: action.payload });
    if (error) {
      return Toast.error(ERRORS.handleDeleteClassroomSaga, error);
    }
    Router.push("/classrooms");
    Toast.success(SUCCESSES.handleDeleteClassroomSaga);
  } catch (error) {
    return Toast.error(ERRORS.handleDeleteClassroomSaga, error.message);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchClassroomSaga(action) {
  const { payload: classroomId } = action;
  yield put(setFetchSpinner(true));
  try {
    if (!classroomId) return false;
    const { data: classroom, error } = yield fetchClassroomApi(classroomId);
    if (error) {
      Toast.error(ERRORS.handleFetchClassroom, error);
    } else {
      yield put(classActions.setActiveClassroom(classroom));
      yield put(setFetchSpinner(false));
      yield put(setFetchDetailsSpinner(true));
      const { data: summary, error: summaryError } = yield fetchClassroomSummaryApi(classroomId);
      if (summaryError) {
        Toast.error(ERRORS.handleFetchClassroomSummary, error);
      } else {
        yield put(classActions.setClassroomSummary(summary, classroomId));
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroom, error);
  } finally {
    yield put(setFetchDetailsSpinner(false));
  }
}

function* handleFetchClassroomOverdueStatsSaga(
  action: classTypes.IHandleFetchClassroomOverdueStats
) {
  try {
    const { classroomId } = action.payload;
    const { data, error } = yield yield fetchClassroomOverdueStats(classroomId);
    if (error) {
    } else {
      yield put(classActions.setClassroomOverdueStats(data));
    }
  } catch (error) {
    Toast.error(error);
  } finally {
  }
}

function* handleFetchClassroomProblemsStatsSaga(
  action: classTypes.IHandleFetchClassroomProblemsStats
) {
  try {
    const { classroomId } = action.payload;
    const { data, error } = yield yield fetchClassroomProblemsStats(classroomId);
    if (error) {
    } else {
      yield put(classActions.setClassroomProblemsStats(data));
    }
  } catch (error) {
    Toast.error(error);
  } finally {
  }
}

function* handleFetchClassroomsSaga(action: classTypes.IHandleFetchClassrooms) {
  yield put(globalActions.setFetchSpinner(true));
  const { isStats } = action.payload;
  try {
    const parameters = yield renderApiParameters(globalSelectors, "classrooms");
    const { meta, data, error } = yield classApi.fetchClassroomsApi(parameters);
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "classrooms"));
    if (error) {
      return Toast.error(ERRORS.handleFetchClassroom, error);
    }
    yield put(classActions.setClassrooms(data));
    yield put(globalActions.setFetchSpinner(false));
    if (isStats) {
      for (const classroom of data as Classroom[]) {
        yield call(handleFetchClassroomStatsSaga, classroom.id);
      }
    }
  } catch (error) {
    yield put(globalActions.setFetchSpinner(false));
    return Toast.error(ERRORS.handleDeleteClassroomSaga, error.message);
  }
}

function* handleFetchClassroomStatsSaga(classroomId: string) {
  try {
    const { data } = yield classApi.fetchClassroomStatsApi(classroomId);
    yield put(classActions.handleSetClassroomStats(data, classroomId));
  } catch (error) {
    return Toast.error(error.message);
  }
}

// This saga is for paginated Lessons, we need lessons for calendar
export function* handleFetchClassroomLessonsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "classroomLessons");
    const classroom = yield select(makeSelectActiveClassroom);
    const { data, error, meta } = yield classApi.fetchClassroomLessonListApi(
      classroom.id,
      `${parameters}&stats=1`
    );
    if (error) {
      Toast.error(ERRORS.handleFetchOneClassLessonList, error);
    } else {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "classroomLessons"));
      yield put(classActions.setClassroomLessons(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchOneClassLessonList, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleFetchClassroomWorksheetsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "classroomWorksheets");
    const activeClassroom = yield select(makeSelectActiveClassroom);
    const { data, meta, error } = yield fetchClassroomWorksheetListApi(
      activeClassroom.id,
      `${parameters}&stats=1`
    );
    // @TODO: THIS ENDPOINT DIRECTS TO stats=1&all=1&, it shoould be managed in a better way
    if (error) {
      Toast.error(ERRORS.handleFetchClassroomWorksheetsSaga, error);
    } else {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "classroomWorksheets"));
    }

    yield put(setClassroomWorksheets(data));
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomWorksheetsSaga, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleFetchClassroomTestsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const activeClassroom = yield select(makeSelectActiveClassroom);
    const parameters = yield renderApiParameters(globalSelectors, "classroomTests");

    const { data, meta, error } = yield classApi.fetchClassroomTestListApi(
      activeClassroom.id,
      `${parameters}&stats=1`
    );
    if (error) {
      Toast.error(ERRORS.handleFetchClassroomTestsSaga, error);
    } else {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "classroomTests"));
      yield put(setClassroomTests(data));
    }
  } catch (error) {
    return Toast.error(ERRORS.handleFetchClassroomTestsSaga, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleRescheduleClassroomWorksheetsSaga(action) {
  yield put(globalActions.setActionSpinner(true));

  try {
    const { payload, source } = action;
    const { error } = yield classApi.rescheduleClassroomWorksheetsApi(payload);
    if (error) {
      Toast.error(ERRORS.handleRescheduleClassroomWorksheetsSaga, error);
    } else {
      if (source === "list") {
        yield call(handleFetchClassroomWorksheetsSaga);
      } else {
        yield call(handleFetchClassroomCalendarWorksheetsSaga);
      }

      Toast.success(SUCCESSES.handleRescheduleClassroomWorksheetsSaga);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleClassroomWorksheetsSaga, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleUnassignWorksheetFromClassroomSaga(
  action: classTypes.IHandleUnassignClassroomWorksheet
) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const activeClassroom: Classroom = yield select(makeSelectActiveClassroom);
    const { classroom_id, classroom_worksheets } = action.payload;
    const worksheetProblemsCount = classroom_worksheets
      .map((worksheet) => worksheet.worksheet.problems_count)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
    const body: UnassignWorksheetFromClassRequest = {
      classroom_id,
      classroom_worksheets: classroom_worksheets.map((worksheet) => worksheet.id)
    };
    const estimateTimeAssigningWorksheets = getEstimateTime(
      worksheetProblemsCount,
      activeClassroom.active_students
    );
    const { error } = yield classApi.unassignWorksheetFromClassApi(body);
    yield put(globalActions.setActionSpinner(false));
    if (error) {
      Toast.error(ERRORS.handleUnassignWorksheetFromClassroomSaga, error);
    } else {
      Toast.info(INFOS.handleUnassignWorksheetToClass, {
        autoClose: estimateTimeAssigningWorksheets,
        position: "bottom-left"
      });
      yield delay(estimateTimeAssigningWorksheets);
      yield put(classActions.handleFetchClassroomWorksheetsList());
    }
  } catch (error) {
    Toast.error(ERRORS.handleUnassignWorksheetFromClassroomSaga, error);
  }
}

/* ================================================== Update process sagas ===================================================== */
/**
 * @params:
 * instructors: table of new instructors
 * */
function* handleAddClassroomInstructor(class_id, instructor_ids) {
  try {
    const { error } = yield addInstructorToClassroomApi({
      class_id,
      instructor_ids
    });
    if (error) {
      Toast.error(ERRORS.handleUpdateClassroomSagaUpdatingInstructors, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateClassroomSagaUpdatingInstructors, error);
  }
}

function* handleAddClassroomInstructorSaga(action) {
  yield put(setActionSpinner(true));
  try {
    yield call(addClassroomInstructor, action.payload);
  } catch (error) {
    Toast.error(ERRORS.handleAddClassroomInstructorSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* addClassroomInstructor(instructors) {
  const activeClassroom = yield select(makeSelectActiveClassroom);
  const newInstructors = removeDuplicateFromArrayObjects(instructors, activeClassroom.instructors);
  if (newInstructors.length > 0) {
    yield call(
      handleAddClassroomInstructor,
      activeClassroom.id,
      newInstructors.map((instructor) => instructor.id)
    );
  }
}

function* handleUpdateClassroomSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const activeClassroom = yield select(makeSelectActiveClassroom);
    const { instructors, students, settings } = action.payload;

    // ARCHIVISATION
    if (activeClassroom.archived !== settings.archived) {
      yield call(handleUpdateClassroomArchivedSaga, {
        payload: {
          classroom_id: activeClassroom.id,
          archived: settings.archived
        }
      });
    }
    const start_date = settings.start_date
      ? moment(settings.start_date).format("YYYY-MM-DD")
      : null;
    const end_date = settings.end_date ? moment(settings.end_date).format("YYYY-MM-DD") : null;
    const { error } = yield classApi.updateClassroomApi({
      ...settings,
      start_date,
      end_date,
      class_id: activeClassroom.id
    });
    if (error) {
      return Toast.error(ERRORS.handleUpdateClass, error);
    }

    // INSTRUCTORS
    if (instructors?.newInstructors?.length) {
      yield call(addClassroomInstructor, instructors.newInstructors);
    }

    if (instructors?.removedInstructors?.length) {
      const { error } = yield removeInstructorFromClassroomApi({
        class_id: activeClassroom.id,
        instructor_ids: instructors.removedInstructors.map((instructor) => instructor.id)
      });

      if (error) {
        Toast.error(ERRORS.handleUpdateClassroomSagaUpdatingInstructors, error);
      }
    }

    // STUDENTS
    if (students.newStudents?.length) {
      let newStudents;

      if (activeClassroom.students?.length) {
        newStudents = removeDuplicateFromArrayObjects(
          students.newStudents,
          activeClassroom.students
        );
      } else {
        newStudents = students.newStudents;
      }

      if (newStudents?.length > 0) {
        const payload = {
          classrooms: [activeClassroom],
          strategy: students.assignStrategy,
          studentIds: newStudents.map((student) => student.id)
        };
        yield call(handleAssignStudentToClassroomSaga, { payload });
      }
    }

    if (students.removedStudents.length) {
      const payload = {
        classrooms: [activeClassroom],
        strategy: students.unassignStrategy,
        studentIds: students.removedStudents.map((student) => student.id)
      };
      yield call(handleRemoveStudentFromClassroomSaga, { payload });
    }

    if (action.source === "LIST") {
      yield put(handleFetchClassrooms(true));
    } else if (action.source === "PROFILE") {
      yield call(handleFetchClassroomSaga, { payload: activeClassroom.id });
    }
    Toast.success(SUCCESSES.handleUpdateClassroomSaga);
  } catch (error) {
    return Toast.error(ERRORS.handleUpdateClass, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateClassroomArchivedSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { classroom_id, archived } = action.payload;
    const { error } = yield classApi.updateClassroomArchivedApi({
      class_id: classroom_id,
      archived
    });
    if (error) {
      return Toast.error(ERRORS.handleUpdateClassArchived, error);
    }
    yield put(globalActions.setActionSpinner(false));
    if (action.source === "list") {
      yield call(handleFetchClassroomsSaga, {
        payload: { isStats: true },
        type: "HANDLE_FETCH_CLASSROOMS"
      });
    } else if (action.source === "profile") {
      yield call(handleFetchClassroomSaga, { payload: classroom_id });
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateClassArchived, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleOpenWorksheetDetailsSaga(worksheet: Worksheet) {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    const { data: classroomWorksheet, error } = yield fetchClassroomWorksheetApi(
      classroom.id,
      worksheet.id
    );
    const { data: template, error: templateError } = yield fetchWorksheetTemplateApi(
      classroomWorksheet.worksheet_id
    );

    if (error || templateError) {
      Toast.error(ERRORS.handleFetchClassroomActiveWorksheetSaga, error || templateError);
    } else {
      const problems = template.problems.map((problem) => ({
        ...problem,
        ...classroomWorksheet.problems.find(
          (worksheetProblems) => worksheetProblems.id === problem.id
        )
      }));
      yield put(
        classActions.setClassroomActiveWorksheet({
          ...classroomWorksheet,
          problems,
          worksheet: template
        })
      );
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomActiveWorksheetSaga);
    yield put(setFetchDetailsSpinner(true));
  }
}

function* handleOpenTestDetailsSaga(test: Test) {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    const { data, error } = yield fetchClassroomTestApi(classroom.id, test.id);
    const { data: testTemplate, error: templateError } = yield fetchTemplateTestApi(data.test_id);

    if (error || templateError) {
      Toast.error(ERRORS.handleFetchClassroomActiveTestSaga, error || templateError);
    } else {
      const sections = [];
      const errors = [];
      for (let section of data.sections) {
        const { data: testSection, error: sectionError } = yield fetchClassroomTestSectionApi(
          classroom.id,
          data.id,
          section["id"]
        );

        if (sectionError) {
          errors.push(sectionError);
        }
        sections.push({
          ...testSection,
          name: section.section
        });
      }

      if (!!errors.length) {
        Toast.error(ERRORS.handleFetchClassroomActiveTestSaga, errors.join(", "));
      } else {
        yield put(classActions.setClassroomActiveTest({ ...data, sections, test: testTemplate }));
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomActiveTestSaga, error);
    yield put(setFetchDetailsSpinner(true));
  }
}

function* handleFetchClassroomStudentsSaga() {
  try {
    const classroom = yield select(makeSelectActiveClassroom);
    yield put(setFetchSpinner(true));
    const parameters = yield renderApiParameters(globalSelectors, "classroomStudents");
    const { data, error, meta } = yield classApi.fetchClassroomStudentsApi(
      classroom.id,
      parameters
    );
    if (error) {
      Toast.error(ERRORS.handleFetchClassroomStudentsSaga, error);
    } else {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "classroomStudents"));
      yield put(classActions.setClassroomStudents(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomActiveTestSaga, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleAssignClassroomAssignmentToStudentsSaga(action) {
  // some spinner needs to be added
  yield put(setActionSpinner(true));
  try {
    const { payload, assignmentType } = action;
    const classroom = yield select(makeSelectActiveClassroom);
    const activeCourse = yield select(globalSelectors.makeSelectActiveCourse);
    let assignment;
    let error;

    switch (assignmentType) {
      case "lesson":
        const { data: classroomLesson, error: lessonError } = yield fetchClassroomLessonApi(
          classroom.id,
          payload
        );
        assignment = classroomLesson;
        error = lessonError;
        break;

      case "worksheet":
        const { data: classroomWorksheet, error: worksheetError } =
          yield fetchClassroomWorksheetApi(classroom.id, payload);
        assignment = classroomWorksheet;
        error = worksheetError;
        break;

      case "test":
        const { data: classroomTest, error: testError } = yield fetchClassroomTestApi(
          classroom.id,
          payload
        );
        assignment = classroomTest;
        error = testError;
        break;
    }
    if (error) {
      Toast.error(ERRORS.handleAssignClassroomAssignmentToStudentsSaga(assignmentType));
    } else {
      const activeStudents = classroom.students.filter((student) =>
        student.courses.find((course) => course.active)
      );
      const unassignedStudents = activeStudents
        .filter(
          (classroomStudent) =>
            !assignment?.students?.find(
              (lessonStudent) => lessonStudent.student_id === classroomStudent.id
            )
        )
        .map((student) => student.id);
      if (!unassignedStudents.length) {
        Toast.info(INFOS.assignClassroomAssignmentToStudent(assignmentType));
      } else {
        Toast.info(INFOS.handleAssignClassroomAssignmentToStudentsSaga);
        const errors = [];
        for (let id of unassignedStudents) {
          const { error: assigningError } = yield assignClassroomAssignmentToStudent(
            {
              student_id: id,
              [`classroom_${assignmentType}_id`]: assignment.id
            },
            assignmentType
          );

          if (assigningError) {
            errors.push(assigningError);
          }
        }
        if (errors.length) {
          if (errors[0]["classroom_test_id" || 0][0] === "Classroom test is assigned to student") {
            Toast.info(INFOS.assignClassroomAssignmentToStudent(assignmentType));
          } else {
            Toast.error(
              ERRORS.handleAssignClassroomAssignmentToStudentsSaga(assignmentType),
              errors.map((error) => Object.values(error).join(", ")).join(", ") || errors.join(", ")
            );
          }
        } else {
          Toast.success(SUCCESSES.assignClassroomAssignmentToStudent(assignmentType));
        }
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleAssignClassroomAssignmentToStudentsSaga(), error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchClassroomWorksheetsByCategoriesSaga(action) {
  yield put(setGlobalTemplateLoader(true));
  try {
    const activeClassroom = yield select(makeSelectActiveClassroom);
    const parameters = yield renderApiParameters(globalSelectors, "worksheetsByCategories");
    const { data, error, meta } = yield fetchClassroomWorksheetByCategoryApi(
      activeClassroom.id,
      `${parameters}&category_id=${action.payload.join(",")}`
    );
    if (error) {
      Toast.error(ERRORS.handleFetchClassroomWorksheetsByCategories, error);
    } else {
      yield put(
        globalActions.setPagination(convertMetaToPagination(meta), "worksheetsByCategories")
      );
      yield put(globalActions.setWorksheetsByCategory(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchClassroomWorksheetsByCategories, error);
  } finally {
    yield put(setGlobalTemplateLoader(false));
  }
}

export default function* classesSaga() {
  yield takeLatest(classTypes.HANDLE_ASSIGN_CLASSROOM_TEST, handleAssignClassroomTestSaga);
  yield takeEvery(classTypes.HANDLE_ASSIGN_CLASSROOM_SESSION, handleAssignSessionToClassroomSaga);
  yield takeEvery(
    classTypes.HANDLE_UNASSIGN_CLASSROOM_LESSON,
    handleUnassignLessonFromClassroomSaga
  );
  yield takeEvery(classTypes.HANDLE_UNASSIGN_CLASSROOM_TEST, handleUnassignTestFromClassroomSaga);
  yield takeEvery(classTypes.HANDLE_RESCHEDULE_CLASSROOM_TESTS, handleRescheduleClassroomTests);

  yield takeLatest(classTypes.HANDLE_UPDATE_CLASSROOM_SESSION, handleUpdateClassroomSessionSaga);

  yield takeLatest(classTypes.HANDLE_FETCH_CLASSROOM_REPORT_PDF, handleFetchClassReportPDFSaga);

  // ================= REFACTORED

  yield takeLatest(
    classTypes.HANDLE_RESCHEDULE_CLASSROOM_LESSONS,
    handleRescheduleClassroomLessonsSaga
  );
  yield takeLatest(classTypes.HANDLE_FETCH_CLASSROOM, handleFetchClassroomSaga);
  yield takeLatest(
    classTypes.HANDLE_ASSIGN_LESSONS_TO_CLASSROOM,
    handleAssignLessonsToClassroomSaga
  );
  yield takeEvery(
    classTypes.HANDLE_DELETE_MULTIPLE_ITEMS_FROM_CLASS,
    handleDeleteMultipleItemsFromClassSaga
  );
  yield takeLatest(
    classTypes.HANDLE_FETCH_CLASSROOM_CALENDAR_DATA,
    handleFetchClassCalendarDataSaga
  );
  yield takeLatest(
    classTypes.HANDLE_OPEN_SELECTED_ITEM_DETAILS_CLASSROOM,
    handleOpenSelectedItemDetailsClassroomSaga
  );
  yield takeLatest(classTypes.HANDLE_DELETE_CLASSROOM, handleDeleteClassroomSaga);
  yield takeLatest(classTypes.HANDLE_UPDATE_CLASSROOM_TEST, handleUpdateClassroomTestSaga);
  yield takeLatest(classTypes.HANDLE_FETCH_CLASSROOMS, handleFetchClassroomsSaga);
  yield takeLatest(classTypes.HANDLE_CREATE_CLASSROOM, handleCreateClassroomSaga);
  yield takeLatest(classTypes.HANDLE_UPDATE_CLASSROOM, handleUpdateClassroomSaga);
  yield takeLatest(classTypes.HANDLE_FETCH_CLASSROOM_LESSONS, handleFetchClassroomLessonsSaga);
  yield takeLatest(
    classTypes.HANDLE_FETCH_CLASSROOM_WORKSHEETS_BY_CATEGORIES,
    handleFetchClassroomWorksheetsByCategoriesSaga
  );
  yield takeLatest(classTypes.HANDLE_UPDATE_CLASSROOM_ARCHIVED, handleUpdateClassroomArchivedSaga);
  yield takeLatest(
    classTypes.HANDLE_FETCH_CLASSROOM_WORKSHEETS_LIST,
    handleFetchClassroomWorksheetsSaga
  );
  yield takeLatest(classTypes.HANDLE_FETCH_CLASSROOM_TESTS_LIST, handleFetchClassroomTestsSaga);
  yield takeLatest(
    classTypes.HANDLE_RESCHEDULE_CLASSROOM_WORKSHEETS,
    handleRescheduleClassroomWorksheetsSaga
  );
  yield takeEvery(
    classTypes.HANDLE_UNASSIGN_CLASSROOM_WORKSHEET,
    handleUnassignWorksheetFromClassroomSaga
  );
  yield takeLatest(
    classTypes.HANDLE_ASSIGN_CLASSROOM_WORKSHEETS,
    handleAssignWorksheetsToClassroomSaga
  );
  yield takeLatest(classTypes.HANDLE_ADD_CLASSROOM_INSTRUCTOR, handleAddClassroomInstructorSaga);
  yield takeLatest(classTypes.HANDLE_FETCH_CLASSROOM_STUDENTS, handleFetchClassroomStudentsSaga);
  /// -------
  yield takeLatest(
    classTypes.HANDLE_ASSIGN_CLASSROOM_ASSIGNMENT_TO_STUDENTS,
    handleAssignClassroomAssignmentToStudentsSaga
  );
  yield takeLatest(
    classTypes.HANDLE_FETCH_CLASSROOM_OVERDUE_STATS,
    handleFetchClassroomOverdueStatsSaga
  );
  yield takeLatest(
    classTypes.HANDLE_FETCH_CLASSROOM_PROBLEMS_STATS,
    handleFetchClassroomProblemsStatsSaga
  );
  //
}
